import {
  type Call,
  type EditorNode,
  fromEditor,
  type Inline,
  type InlineRun,
  type Mark,
  toEditor,
} from '@app/studio'
import {
  createNodeFromContent,
  type Extensions,
  getSchema,
  Node,
  Mark as TiptapMark,
} from '@tiptap/core'
import { EditorContent, useEditor } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'
import { Notice } from './index'
import './run-editor.css'

type Extension = Extensions[number]

/**
 * A writing surface for one inline run of a book.
 *
 * It renders as INLINE content, so it sits inside the markup the reader already
 * produced — the book's own paragraph, verse line or citation — and the words
 * keep the face, measure and direction they are published in. A surface that
 * drew its own box would mean editing a paragraph that does not look like the
 * one on the page, which is the same fault as a luach that changes face between
 * the screen and the sheet.
 *
 * Prose rather than a field per span: a person edits the words, and the spans,
 * marks and markers around them are carried through untouched. A field per span
 * cannot delete a sentence that crosses a boundary, the most ordinary edit
 * there is.
 *
 * **The marks are the book's, not a word processor's.** `emphasis` is what the
 * author meant and italic is only how English says it, so the schema carries
 * the meaning and the page decides how it is set.
 */

/** The whole document is one run, so its top node holds inline content only. */
const Doc = Node.create({ name: 'doc', topNode: true, content: 'line' })

/**
 * The line the run sits on, drawn as a span.
 *
 * A span because this editor is mounted INSIDE the book's own block element,
 * and a paragraph there would be a second one nested in the first.
 */
const Line = Node.create({
  name: 'line',
  content: 'inline*',
  renderHTML: () => ['span', 0],
  parseHTML: () => [{ tag: 'span' }],
})

const Text = Node.create({ name: 'text', group: 'inline' })

/** A break WITHIN one thought. Between thoughts, the book uses separate blocks. */
const Break = Node.create({
  name: 'hardBreak',
  inline: true,
  group: 'inline',
  selectable: false,
  renderHTML: () => ['br'],
  parseHTML: () => [{ tag: 'br' }],
})

/** A language span. A mark here, a field in the model — a node carries only marks. */
const LangMark = TiptapMark.create({
  name: 'lang',
  addAttributes: () => ({ lang: { default: 'en' } }),
  parseHTML: () => [{ tag: 'span[data-lang]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'span',
    { 'data-lang': HTMLAttributes.lang, lang: HTMLAttributes.lang },
    0,
  ],
})

/** The book's four marks, each carrying a meaning rather than an appearance. */
const bookMark = (name: Mark) =>
  TiptapMark.create({
    name,
    parseHTML: () => [{ tag: `span[data-mark="${name}"]` }],
    renderHTML: () => ['span', { 'data-mark': name }, 0],
  })

/**
 * A reference the author may move or delete but can never author.
 *
 * Atomic, so its id cannot be typed over — the id names a real row, and a
 * marker that could be retyped is one that could be made to point at nothing.
 * Deleting one is an ordinary edit, refused server-side where it would strand
 * what it names.
 */
const reference = (name: string, attrs: string[], glyph: string) =>
  Node.create({
    name,
    inline: true,
    group: 'inline',
    atom: true,
    selectable: true,
    addAttributes: () =>
      Object.fromEntries(attrs.map((a) => [a, { default: null }])),
    parseHTML: () => [{ tag: `span[data-ref="${name}"]` }],
    renderHTML: () => [
      'span',
      { 'data-ref': name, class: 'run-ref', contenteditable: 'false' },
      glyph,
    ],
  })

/*
 * One module-level set: `useEditor` re-applies its options on every render, and
 * a fresh array re-registers every extension — which trips a duplicate name and
 * leaves the schema null.
 */
const MARK_EXTENSIONS = {
  emphasis: bookMark('emphasis'),
  strong: bookMark('strong'),
  smallcaps: bookMark('smallcaps'),
  transliteration: bookMark('transliteration'),
} satisfies Record<Mark, Extension>

const INLINE_EXTENSIONS = {
  text: [Text, LangMark, ...Object.values(MARK_EXTENSIONS)],
  /* One atom where there were three. `of` is what the words carry and decides
     the glyph; `id` is what it names, held here so the editor knows which call
     it has hold of — deleting one of two markers is otherwise unanswerable. It
     never reaches the stored JSON. */
  call: [reference('call', ['of', 'id'], '※')],
  break: [Break],
} satisfies Record<Inline['t'], Extensions>

const EXTENSIONS: Extensions = [
  Doc,
  Line,
  ...Object.values(INLINE_EXTENSIONS).flat(),
]

type RunDocument = {
  type: string
  content: { type: string; content: EditorNode[] }[]
}

const asDoc = (run: InlineRun, calls: Call[]): RunDocument => {
  /* Runtime content may come from a newer schema, so the discriminant widens
     while the installed extension record remains exhaustive for this build. */
  const unknown = (run as { t: string }[]).find(
    (node) => !Object.hasOwn(INLINE_EXTENSIONS, node.t),
  )

  return {
    type: 'doc',
    content: [
      {
        type: 'line',
        /* Let Tiptap see an unrecognized runtime kind by its real name. Calling
           `toEditor` first would throw at its compile-time exhaustiveness guard,
           before Tiptap can refuse content received from a newer schema. */
        content: unknown
          ? ([{ type: unknown.t }] as EditorNode[])
          : toEditor(run, calls),
      },
    ],
  }
}

const invalidKind = (error: unknown): string | null => {
  for (let cause = error; cause instanceof Error; cause = cause.cause) {
    const named =
      cause.message.match(/Unknown node type: ([^\s]+)/)?.[1] ??
      cause.message.match(/There is no mark type ([^\s]+) in this schema/)?.[1]
    if (named) return named
  }
  return null
}

/** Tiptap's own schema verdict over the exact extension set the editor uses. */
export function runContentRefusal(
  run: InlineRun,
  calls: Call[] = [],
): string | null {
  try {
    createNodeFromContent(asDoc(run, calls), getSchema(EXTENSIONS), {
      errorOnInvalidContent: true,
      slice: false,
    })
    return null
  } catch (error) {
    return invalidKind(error) ?? 'unknown'
  }
}

/** The save boundary shared by the editor and the passage that contains it. */
export function commitRepresentableContent(
  refused: boolean,
  commit: (() => void) | undefined,
): boolean {
  if (refused || !commit) return false
  commit()
  return true
}

/** One writing surface's set of runs the installed editor cannot represent. */
export function useContentRefusal() {
  const [refusedRuns, setRefusedRuns] = useState<Set<string>>(() => new Set())
  const markRefusal = (path: string, refused: boolean) =>
    setRefusedRuns((held) => {
      if (held.has(path) === refused) return held
      const next = new Set(held)
      if (refused) next.add(path)
      else next.delete(path)
      return next
    })

  return { refusing: refusedRuns.size > 0, markRefusal }
}

/**
 * Read the run back out.
 *
 * An empty run is a real answer — a passage edited down to nothing — so an
 * absent `content` is an empty array rather than a reason to keep what was
 * there.
 */
const asRun = (doc: { content?: { content?: EditorNode[] }[] }) =>
  fromEditor(doc.content?.[0]?.content ?? [])

export function RunEditor({
  value,
  calls,
  onValueChange,
  autoFocus,
  className,
  lang,
  dir,
  placeholder,
  onCommit,
  onCancel,
  onContentRefusalChange,
}: {
  value: InlineRun
  /**
   * What this run's markers name, in order.
   *
   * Handed in so the editor can hand them back: the words say a call sits here
   * and the rows say which, so an edit that moves or deletes a marker carries
   * its call rather than leaving the save to guess.
   */
  calls?: Call[]
  onValueChange: (run: InlineRun, calls: Call[]) => void
  autoFocus?: boolean
  /**
   * The class the book gives this line, so the words keep their setting.
   *
   * The editable element is the book's own paragraph or verse line rather than
   * a box inside it: nested, the text sits in a second block and the
   * paragraph's indent and leading apply twice, so opening a passage moves it.
   */
  className?: string
  lang?: string
  dir?: string
  /**
   * What an empty run is FOR, shown in its place until somebody writes one.
   *
   * An empty line with nothing in it reads as space the layout left behind
   * rather than as somewhere to write — which is what makes a subtitle a
   * section has never had impossible to discover.
   */
  placeholder?: string
  /**
   * Commit and abandon, reachable from the keyboard.
   *
   * A writing surface has to be finishable without a mouse: ⌘↵ commits, which
   * is what the comment band already means by it, and Escape leaves without
   * writing. Without them the only way out of a passage is to find a button,
   * which is not an operable control — it is a mouse target with a caret in
   * front of it.
   */
  onCommit?: () => void
  onCancel?: () => void
  /** Whether this run can be represented by the installed editor schema. */
  onContentRefusalChange?: (refused: boolean) => void
}) {
  const content = asDoc(value, calls ?? [])
  const [refusedKind, setRefusedKind] = useState<string | null>(() =>
    runContentRefusal(value, calls),
  )
  const refusalCallback = useRef(onContentRefusalChange)
  refusalCallback.current = onContentRefusalChange

  const editor = useEditor({
    extensions: EXTENSIONS,
    content,
    enableContentCheck: true,
    editable: !refusedKind,
    onContentError: ({ editor, error }) => {
      setRefusedKind(invalidKind(error) ?? 'unknown')
      editor.setEditable(false, false)
    },
    autofocus: autoFocus ? 'end' : false,
    /*
     * The editable element carries NO book class, and that is the whole of why
     * a lead paragraph keeps its setting.
     *
     * The book zeroes a first paragraph's indent through a structural selector
     * — the passage's first child — and the class sits on the mount below,
     * which is that child. Declaring it here as well would re-apply the default
     * indent to the inner box and win over what the structure decided.
     * Everything that matters inherits: the face, the size, the leading and the
     * indent.
     */
    editorProps: {
      attributes: { class: 'run-editor' },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Escape' && onCancel) {
          onCancel()
          return true
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          commitRepresentableContent(!!refusedKind, onCommit)
          return true
        }
        /* A bare Enter would open a second line the model has no room for: one
           run is one line, and a break WITHIN a thought is ⇧↵. Between
           thoughts the book uses separate blocks, which this surface does not
           make. */
        if (event.key === 'Enter' && !event.shiftKey) return true
        return false
      },
    },
    onUpdate: ({ editor }) =>
      (() => {
        const { run, calls } = asRun(
          editor.getJSON() as Parameters<typeof asRun>[0],
        )
        onValueChange(run, calls)
      })(),
  })

  useEffect(() => {
    refusalCallback.current?.(!!refusedKind)
  }, [refusedKind])

  useEffect(
    () => () => {
      refusalCallback.current?.(false)
    },
    [],
  )

  /* Set from outside only where it genuinely differs, and never while somebody
     is typing into it. Comparing the RUN rather than the document asks the same
     question the save asks. */
  useEffect(() => {
    if (!editor || editor.isDestroyed || editor.isFocused) return
    const nextRefusal = runContentRefusal(value, calls)
    setRefusedKind(nextRefusal)
    editor.setEditable(!nextRefusal, false)
    if (nextRefusal) return

    const held = asRun(editor.getJSON() as Parameters<typeof asRun>[0])
    if (JSON.stringify(held.run) !== JSON.stringify(value)) {
      editor.commands.setContent(asDoc(value, calls ?? []), {
        emitUpdate: false,
      })
    }
  }, [value, calls, editor])

  if (refusedKind) {
    return (
      <Notice tone="error">
        This passage cannot be edited because it contains the unsupported inline
        kind “{refusedKind}”. Nothing has been saved.
      </Notice>
    )
  }

  if (!editor) return null
  /*
   * The mount IS the book's own element — its paragraph, its verse line — and
   * stands exactly where that element stood, as the passage's first child. That
   * is what keeps the structural rules that decide an indent still matching.
   */
  return (
    <EditorContent
      editor={editor}
      className={
        className ? `run-editor__mount ${className}` : 'run-editor__mount'
      }
      {...(lang ? { lang } : {})}
      {...(dir ? { dir } : {})}
      {...(placeholder && !value.length
        ? { 'data-placeholder': placeholder }
        : {})}
    />
  )
}
